import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { nav as copy } from '../content/copy';
import { WHATSAPP_INVITE_EVENT, getWhatsappInvite } from '../lib/session';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
  /** Hide the nav on focused surfaces (welcome page, admin). */
  bare?: boolean;
}

/**
 * The card. A referee's card is the whole mark — no logo file, no image
 * request, just a cream rectangle at card proportions. Cheapest possible
 * wordmark for a page with a Lighthouse budget, and it means the brand survives
 * a restyle by changing one token.
 */
function Wordmark({ small = false }: { small?: boolean }) {
  return (
    <span className={small ? 'wordmark wordmark--small' : 'wordmark'}>
      <span className="wordmark__card" aria-hidden="true" />
      <span className="wordmark__text">Ref&rsquo;s a Banker</span>
    </span>
  );
}

export default function Layout({ children, bare = false }: LayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Null until /league has been unlocked in this tab; see lib/session.ts for
  // why the footer reads this rather than fetching or hardcoding anything.
  const [invite, setInvite] = useState<string | null>(() => getWhatsappInvite());

  useEffect(() => {
    const sync = () => setInvite(getWhatsappInvite());
    window.addEventListener(WHATSAPP_INVITE_EVENT, sync);
    return () => window.removeEventListener(WHATSAPP_INVITE_EVENT, sync);
  }, []);

  return (
    <div className="page">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {!bare && (
        <header className="masthead">
          <div className="masthead__inner">
            <Link to="/" className="masthead__brand" onClick={() => setMenuOpen(false)}>
              <Wordmark />
            </Link>

            {/*
              Below ~720px the four nav items stop fitting beside the wordmark,
              so they collapse behind this. Above it the button is display:none
              and the list is always visible — no JS decides the layout, only
              whether the collapsed list is expanded.
            */}
            <button
              type="button"
              className="masthead__burger"
              aria-expanded={menuOpen}
              aria-controls="main-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="visually-hidden">
                {menuOpen ? 'Close menu' : 'Open menu'}
              </span>
              <span className="masthead__burger-bars" aria-hidden="true" />
            </button>

            <nav
              id="main-nav"
              className={menuOpen ? 'masthead__nav masthead__nav--open' : 'masthead__nav'}
              aria-label="Main"
            >
              {copy.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    isActive ? 'masthead__link masthead__link--on' : 'masthead__link'
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
              <NavLink
                to="/join"
                className="masthead__cta"
                onClick={() => setMenuOpen(false)}
              >
                Join
              </NavLink>
            </nav>
          </div>
        </header>
      )}

      <main id="main" className="page__body">
        {children}
      </main>

      <footer className="footer">
        <div className="wrap wrap--wide">
          <div className="footer__cols">
            <div>
              <Wordmark small />
              <p className="footer__blurb">{copy.footerBlurb}</p>
            </div>

            <nav className="footer__links" aria-label="Footer">
              {copy.links.map((link) => (
                <Link key={link.to} to={link.to}>
                  {link.label}
                </Link>
              ))}
              <Link to="/join">Join</Link>
            </nav>

            <div className="footer__links">
              <a href="mailto:theref@refsabanker.com">theref@refsabanker.com</a>
              {/*
                Rules point at /league: they live there in the open, because
                they are recruitment material and gating them would be a
                downgrade.

                The group link is a real WhatsApp href only for someone this
                tab has already seen through the password gate — getStandings
                handed us the invite and we kept it. Everyone else is sent to
                /league, which is where that gate is. What must never happen is
                the URL arriving as a literal in this file: every route serves
                this same footer, so that is the exact regression
                verify:bundle exists to catch.
              */}
              {invite ? (
                <a href={invite} target="_blank" rel="noreferrer noopener">
                  The WhatsApp group
                </a>
              ) : (
                <Link to="/league">The WhatsApp group</Link>
              )}
              <Link to="/league">Rules &amp; disputes</Link>
            </div>
          </div>

          <p className="footer__fineprint">{copy.fineprint}</p>
        </div>
      </footer>
    </div>
  );
}
